import { createApi } from '@reduxjs/toolkit/query/react'
import { store } from 'app/store'
import { API_ENDPOINTS } from 'Constants'
import {
  updateProposalButtonLoadingStatus,
  updateProposalSuccessResponse,
  updateProposalErrorResponse,
  updatePolicyDataFromOCR,
} from 'features/Motor'
import { IGetCkycNumber, IQuotationRequest, TMotorProposalPayload } from 'Models/RequestModels'
import {
  IAdditionalCovers,
  IAddOns,
  IInsurer,
  IManufacturer,
  IModel,
  IPincode,
  IVariant,
  IApiResponse,
  IAuthenticatedUser,
  IGetPollingQuotation,
  IPostPollingQuotation,
  IProposal,
  IPlanDetails,
  IVahan,
  IMLPolicyResponse,
  IPaymentCheck,
  IGetCkycNumberResponse,
  IHypothecation,
} from 'Models/ResponseModels'
import { IValidateInsurerResponse } from 'Models/ResponseModels/motor/GetCkycNumber'
import { baseUrlQuery, getFileHeaders } from 'Services/baseQuery'
import { ToastService } from 'Services/toast.service'

export const motorApi = createApi({
  reducerPath: 'motorApi',
  baseQuery: baseUrlQuery,
  endpoints: (builder) => ({
    getManufacturers: builder.query<IManufacturer[], { type: string }>({
      query: (vehicleType: { type: string }) => ({
        url: API_ENDPOINTS.MANUFACTURERS,
        method: 'GET',
        params: vehicleType,
      }),
    }),
    getModels: builder.query<IModel[], { manufacturer: string }>({
      query: (selectedManufacturerCode: { manufacturer: string }) => ({
        url: API_ENDPOINTS.MODELS,
        method: 'GET',
        params: selectedManufacturerCode,
      }),
    }),
    getVariants: builder.query<IVariant[], { model: string }>({
      query: (selectedModelCode: { model: string }) => ({
        url: API_ENDPOINTS.VARIANTS,
        method: 'GET',
        params: selectedModelCode,
      }),
    }),
    getInsurers: builder.query<IInsurer[], { insurer_type: string }>({
      query: (insurerType: { insurer_type: string }) => ({
        url: API_ENDPOINTS.INSURERS,
        method: 'GET',
        params: insurerType,
      }),
    }),
    getAdditionalCovers: builder.query<IAdditionalCovers, { variable_type: string }>({
      query: (type: { variable_type: string }) => ({
        url: API_ENDPOINTS.ADDITIONAL_COVERS,
        method: 'GET',
        params: type,
      }),
      transformResponse: (response: IAdditionalCovers) => {
        Object.keys(response).map((cover) => {
          response[cover].map((c) => {
            c.isSelected = false
          })
        })
        return response
      },
    }),
    getAddons: builder.query<IAddOns, { variable_type: string }>({
      query: (type: { variable_type: string }) => ({
        url: API_ENDPOINTS.ADD_ONS,
        method: 'GET',
        params: type,
      }),
    }),
    postPollingQuotation: builder.mutation<IPostPollingQuotation, IQuotationRequest>({
      query: (payload: IQuotationRequest) => ({
        url: API_ENDPOINTS.MOTOR_QUOTATIONS,
        method: 'POST',
        body: payload,
      }),
    }),
    getPollingQuotation: builder.query<IGetPollingQuotation, { sub_request_id: string }>({
      query: (type: { sub_request_id: string }) => ({
        url: API_ENDPOINTS.MOTOR_QUOTATIONS,
        method: 'GET',
        params: type,
      }),
    }),
    getPlanDetails: builder.query<IPlanDetails, { insurance_code: string; policy_type: string }>({
      query: (type: { insurance_code: string; policy_type: string }) => ({
        url: API_ENDPOINTS.PLAN_DETAILS,
        method: 'GET',
        params: type,
      }),
    }),
    validateRto: builder.query<{ [key: string]: string }, { keyword: string }>({
      query: (rtoObj: { keyword: string }) => ({
        url: API_ENDPOINTS.RTO,
        method: 'GET',
        params: rtoObj,
      }),
    }),
    getPincode: builder.query<IPincode[], { pincode: number; insurance_id: number }>({
      query: (pincodeParams: { pincode: number; insurance_id: number }) => ({
        url: API_ENDPOINTS.PINCODE_CMS,
        method: 'GET',
        params: pincodeParams,
      }),
    }),
    getOccupations: builder.query<{ [key: number]: string }, void>({
      query: () => ({
        url: API_ENDPOINTS.OCCUPATIONS,
        method: 'GET',
      }),
    }),
    getRelationships: builder.query<{ [key: number]: string }, void>({
      query: () => ({
        url: API_ENDPOINTS.RELATIONSHIPS,
        method: 'GET',
      }),
    }),
    postProposal: builder.mutation<IProposal, TMotorProposalPayload>({
      query: (proposalPayload: TMotorProposalPayload) => ({
        url: API_ENDPOINTS.MOTOR_PROPOSAL,
        method: 'POST',
        body: proposalPayload,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(updateProposalButtonLoadingStatus('loading'))
          const { data: proposalResponse } = await queryFulfilled
          dispatch(updateProposalSuccessResponse(proposalResponse))
          dispatch(updateProposalButtonLoadingStatus('success'))
          console.log(proposalResponse)
        } catch (err: any) {
          console.log('Errrr...', err['error']['message'])
          // dispatch error string of proposal response
          dispatch(updateProposalButtonLoadingStatus('error'))
          dispatch(updateProposalErrorResponse(err['error']['message']))
        }
      },
    }),
    paymentCheck: builder.query<IPaymentCheck, { transaction_id: string }>({
      query: (transactionId: { transaction_id: string }) => ({
        url: API_ENDPOINTS.PAYMENT_CHECK,
        method: 'GET',
        params: transactionId,
      }),
    }),
    vahanPrefill: builder.mutation<IVahan, { category: number; registration_no: string }>({
      query: (vahanPayload: { category: number; registration_no: string }) => ({
        url: API_ENDPOINTS.VAHAN_CHECK,
        method: 'POST',
        body: vahanPayload,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          console.log(data)
          // dispatch()
        } catch (e) {
          console.log(e)
        }
      },
    }),
    uploadImageRC: builder.mutation<any, FormData>({
      query: (filePayload: FormData) => {
        const token: string = store.getState().user.authenticatedUser.authtoken ?? ''
        return {
          url: API_ENDPOINTS.IMAGE_RC_OCR,
          method: 'POST',
          body: filePayload,
          headers: getFileHeaders(token),
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (e: any) {
          ToastService.showErrorToast({
            title: 'Error',
            message: e['error']['message'],
          })
        }
      },
    }),
    ckycValidateInsurer: builder.query<IValidateInsurerResponse, {insurance_code: string}>({
      query: (params: {insurance_code: string}) => ({
        url: API_ENDPOINTS.CKYC_VALIDATE,
        method: 'GET',
        params: params
      })
    }),
    getCkycNumber: builder.mutation<IGetCkycNumberResponse, IGetCkycNumber>({
      query: (payload: IGetCkycNumber) => ({
        url: API_ENDPOINTS.CKYC_NUMBER,
        method: 'POST',
        body: payload
      })
    }),
    uploadCkycValidationData: builder.mutation<IGetCkycNumberResponse, FormData>({
      query: (payload: FormData) => ({
        url: API_ENDPOINTS.CKYC_NUMBER,
        method: 'POST',
        body: payload
       })
    }),
    uploadPolicyPdf: builder.mutation<IMLPolicyResponse, FormData>({
      query: (filePayload: FormData) => {
        const token: string = store.getState().user.authenticatedUser.authtoken ?? ''
        return {
          url: API_ENDPOINTS.IMAGE_POLICY_OCR,
          method: 'POST',
          body: filePayload,
          headers: getFileHeaders(token),
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled
          const { vehicleNumber } = store.getState().homePage
          const registrationNoFilled = vehicleNumber.replaceAll('-', '')
          const registrationNoFromOcr = response.data.registration_number
          console.log('registrationNoFilled - ', registrationNoFilled)
          console.log('registrationNoFromOcr - ', registrationNoFromOcr)
          if (registrationNoFilled.toLowerCase() === registrationNoFromOcr?.toLowerCase()) {
            dispatch(updatePolicyDataFromOCR(response.data))
          } else {
            ToastService.showErrorToast({
              title: 'Registration no. entered does not match with Policy copy uploaded',
              message: 'Please check the details before proceeding further',
            })
          }
        } catch (e: any) {
          ToastService.showErrorToast({
            title: 'Error',
            message: e['error']['message'],
          })
        }
      },
    }),
    getHypothecation: builder.query<
      IHypothecation,
      { bank_name: string; insurance_id: string | number; category: string }
    >({
      query: (hypothecationParams: {
        bank_name: string
        insurance_id: string | number
        category: string
      }) => ({
        url: API_ENDPOINTS.HYPOTHECATION,
        method: 'GET',
        params: hypothecationParams,
      }),
    }),
  }),
})

export const {
  useGetManufacturersQuery,
  useLazyGetManufacturersQuery,
  useGetModelsQuery,
  useLazyGetModelsQuery,
  useGetVariantsQuery,
  useLazyGetVariantsQuery,
  useGetAdditionalCoversQuery,
  useGetAddonsQuery,
  useGetInsurersQuery,
  useLazyGetInsurersQuery,
  useGetPollingQuotationQuery,
  usePostPollingQuotationMutation,
  useGetPlanDetailsQuery,
  useLazyValidateRtoQuery,
  useLazyGetPincodeQuery,
  useGetOccupationsQuery,
  useGetRelationshipsQuery,
  usePostProposalMutation,
  useVahanPrefillMutation,
  useUploadImageRCMutation,
  useUploadPolicyPdfMutation,
  useLazyPaymentCheckQuery,
  useGetCkycNumberMutation,
  useLazyCkycValidateInsurerQuery,
  useUploadCkycValidationDataMutation,
  useGetHypothecationQuery,
} = motorApi
